import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { fetchAuthSession } from 'aws-amplify/auth';
import { from, map, Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import {
  createGraphqlClientError,
  toGraphqlClientError
} from 'src/entities/order/lib/graphql-error.utils';

@Injectable({
  providedIn: 'root',
})
export class GraphService {
  constructor(private http: HttpClient) {}

  private async getAuthHeaders(): Promise<HttpHeaders> {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      if (token) {
        return new HttpHeaders({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        });
      } else {
        console.warn('⚠️ No hay token disponible para autenticación');
        return new HttpHeaders({
          'Content-Type': 'application/json',
        });
      }
    } catch (error) {
      console.error('❌ Error obteniendo token para headers:', error);
      return new HttpHeaders({
        'Content-Type': 'application/json',
      });
    }
  }

  public queryGraphQL<T>(options: {
    query: string;
    variables?: { [key: string]: any };
  }): Observable<T> {
    return from(this.getAuthHeaders()).pipe(
      switchMap((headers) => {
        return this.http
          .post<{ data: T }>(
            `${environment.operationsApi}/graphql`,
            {
              query: options.query,
              variables: options.variables,
            },
            { headers }
          )
          .pipe(
            map((d) => d.data),
            catchError((error) => {
              console.error('❌ Error en petición a Operations API:', error);
              return throwError(error);
            })
          );
      })
    );
  }

  public queryGraphQLAlmacen<T>(options: {
    query: string;
    variables?: { [key: string]: any };
  }): Observable<T> {
    return from(this.getAuthHeaders()).pipe(
      switchMap((headers) => {
        return this.http
          .post<{ data: T }>(
            `${environment.warehouseApi}/graphql`,
            {
              query: options.query,
              variables: options.variables,
            },
            { headers }
          )
          .pipe(
            map((d) => d.data),
            catchError((error) => {
              console.error('❌ Error en petición a Warehouse API:', error);
              return throwError(error);
            })
          );
      })
    );
  }

  public queryGraphQLSourcing<T>(options: {
    query: string;
    variables?: { [key: string]: any };
  }): Observable<T> {
    return this.queryGraphQLSourcingEnvelope<T>(options).pipe(
      map((envelope) => envelope.data as T),
      catchError((error) => {
        console.error('❌ Error en petición a Sourcing API:', error);
        return throwError(error);
      })
    );
  }

  /** Respuesta cruda Sourcing (`data` + `errors`) para contratos envelope. */
  public queryGraphQLSourcingEnvelope<T>(options: {
    query: string;
    variables?: { [key: string]: any };
  }): Observable<{
    data: T | null;
    errors?: Array<{
      message?: string;
      extensions?: {
        response?: { code?: string; message?: string };
        code?: string;
      };
    }>;
  }> {
    return from(this.getAuthHeaders()).pipe(
      switchMap((headers) => {
        return this.http
          .post<{
            data: T | null;
            errors?: Array<{
              message?: string;
              extensions?: {
                response?: { code?: string; message?: string };
                code?: string;
              };
            }>;
          }>(
            `${environment.sourcingApi}/graphql`,
            {
              query: options.query,
              variables: options.variables,
            },
            { headers }
          )
          .pipe(
            map((d) => ({
              data: d?.data ?? null,
              errors: d?.errors,
            })),
            catchError((error) => {
              console.error('❌ Error en petición a Sourcing API:', error);
              return throwError(error);
            })
          );
      })
    );
  }

  public queryGraphQLSales<T>(options: {
    query: string;
    variables?: { [key: string]: any };
  }): Observable<T> {
    return from(this.getAuthHeaders()).pipe(
      switchMap((headers) => {
        return this.http
          .post<{ data: T }>(
            `${environment.salesApi}/graphql`,
            {
              query: options.query,
              variables: options.variables,
            },
            { headers }
          )
          .pipe(
            map((response: any) => {
              if (response.errors) {
                return {
                  data: response.data,
                  errors: response.errors,
                };
              }
              return response.data;
            }),
            catchError((error) => {
              console.error('❌ Error en petición a Sales API:', error);
              return throwError(
                () =>
                  new Error(
                    error.message ||
                      'Hubo un error ,por favor intentelo mas tarde.'
                  )
              );
            })
          );
      })
    );
  }

  public queryGraphQLCore<T>(options: {
    query: string;
    variables?: { [key: string]: any };
  }): Observable<T> {
    return from(this.getAuthHeaders()).pipe(
      switchMap((headers) => {
        return this.http
          .post<{ data: T }>(
            `${environment.coreApi}/graphql`,
            {
              query: options.query,
              variables: options.variables,
            },
            { headers }
          )
          .pipe(
            map((response: any) => {
              if (response.errors) {
                return {
                  data: response.data,
                  errors: response.errors,
                };
              }
              return response.data;
            }),
            catchError((error) => {
              console.error('❌ Error en petición a Core API:', error);
              return throwError(
                () =>
                  new Error(
                    error.message ||
                      'Hubo un error ,por favor intentelo mas tarde.'
                  )
              );
            })
          );
      })
    );
  }

  public queryGraphQLProduct<T>(options: {
    query: string;
    variables?: { [key: string]: any };
  }): Observable<T> {
    return from(this.getAuthHeaders()).pipe(
      switchMap((headers) => {
        return this.http
          .post<{ data: T }>(
            `${environment.productApi}/graphql`,
            {
              query: options.query,
              variables: options.variables,
            },
            { headers }
          )
          .pipe(
            map((d) => d.data),
            catchError((error) => {
              console.error('❌ Error en petición a Product API:', error);
              return throwError(error);
            })
          );
      })
    );
  }

  public mutateGraphQL<T>(options: {
    mutation: string;
    variables?: { [key: string]: any };
  }): Observable<any> {
    return from(this.getAuthHeaders()).pipe(
      switchMap((headers) => {
        return this.http
          .post<{ data: T }>(
            `${environment.operationsApi}/graphql`,
            {
              query: options.mutation,
              variables: options.variables,
            },
            { headers }
          )
          .pipe(
            map((d) => d.data),
            catchError((error) => {
              console.error('❌ Error en mutación a Operations API:', error);
              return throwError(error);
            })
          );
      })
    );
  }

  public mutateGraphQLCore<T>(options: {
    mutation: string;
    variables?: { [key: string]: any };
  }): Observable<any> {
    return from(this.getAuthHeaders()).pipe(
      switchMap((headers) => {
        return this.http
          .post<any>(
            `${environment.coreApi}/graphql`,
            {
              query: options.mutation,
              variables: options.variables,
            },
            { headers }
          )
          .pipe(
            map((response: any) => {
              if (response.errors && response.errors.length > 0) {
                throw createGraphqlClientError(response.errors);
              }
              return response.data;
            }),
            catchError((error) => {
              console.error('❌ Error en mutación a Core API:', error);
              return throwError(() => toGraphqlClientError(error));
            })
          );
      })
    );
  }

  public mutateGraphQLSales<T>(options: {
    mutation: string;
    variables?: { [key: string]: any };
  }): Observable<any> {
    return from(this.getAuthHeaders()).pipe(
      switchMap((headers) => {
        return this.http
          .post<any>(
            `${environment.salesApi}/graphql`,
            {
              query: options.mutation,
              variables: options.variables,
            },
            { headers }
          )
          .pipe(
            map((response: any) => {
              if (response.errors && response.errors.length > 0) {
                throw new Error(response.errors[0].message);
              }
              return response.data;
            }),
            catchError((error) => {
              console.error('❌ Error en mutación a Sales API:', error);
              return throwError(() => error);
            })
          );
      })
    );
  }

  public mutateGraphQLAlmacen<T>(options: {
    mutation: string;
    variables?: { [key: string]: any };
  }): Observable<any> {
    return from(this.getAuthHeaders()).pipe(
      switchMap((headers) => {
        return this.http
          .post<{ data: T }>(
            `${environment.warehouseApi}/graphql`,
            {
              query: options.mutation,
              variables: options.variables,
            },
            { headers }
          )
          .pipe(
            map((d) => d.data),
            catchError((error) => {
              console.error('❌ Error en mutación a Warehouse API:', error);
              return throwError(error);
            })
          );
      })
    );
  }

  public mutateGraphQLSoucing<T>(options: {
    mutation: string;
    variables?: { [key: string]: any };
  }): Observable<any> {
    return from(this.getAuthHeaders()).pipe(
      switchMap((headers) => {
        return this.http
          .post<{ data: T }>(
            `${environment.sourcingApi}/graphql`,
            {
              query: options.mutation,
              variables: options.variables,
            },
            { headers }
          )
          .pipe(
            map((d) => d.data),
            catchError((error) => {
              console.error('❌ Error en mutación a Sourcing API:', error);
              return throwError(error);
            })
          );
      })
    );
  }

  //financials
  public queryGraphQLFinancials<T>(options: {
    query: string;
    variables?: { [key: string]: any };
  }): Observable<T> {
    return from(this.getAuthHeaders()).pipe(
      switchMap((headers) => {
        return this.http
          .post<{ data: T }>(
            `${environment.financialsApi}/graphql`,
            {
              query: options.query,
              variables: options.variables,
            },
            { headers }
          )
          .pipe(
            map((d) => d.data),
            catchError((error) => {
              console.error('❌ Error en petición a Financials API:', error);
              return throwError(error);
            })
          );
      })
    );
  }

  public mutateGraphQLFinancials<T>(options: {
    mutation: string;
    variables?: { [key: string]: any };
  }): Observable<any> {
    return from(this.getAuthHeaders()).pipe(
      switchMap((headers) => {
        return this.http
          .post<{ data: T }>(
            `${environment.financialsApi}/graphql`,
            {
              query: options.mutation,
              variables: options.variables,
            },
            { headers }
          )
          .pipe(
            map((d) => d.data),
            catchError((error) => {
              console.error('❌ Error en mutación a Financials API:', error);
              return throwError(error);
            })
          );
      })
    );
  }
}
